import { Component, Inject, OnInit } from '@angular/core';
import { AbstractControl, FormArray, FormBuilder, FormGroup, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { ContentDialogGenericComponent } from '@app/shared/components/content-dialog-generic/content-dialog-generic.component';
import { IProgram, IRetailer, IRetailerProgram } from '@app/shared/interfaces';
import { ToastrService } from 'ngx-toastr';
import { RetailersService } from '../../services/retailers/retailers.service';

interface IRetailerDialog extends IRetailer {
  action?: 'edit' | 'create';
}

const requiredTrimmed: ValidatorFn = (control: AbstractControl): ValidationErrors | null => {
  return typeof control.value === 'string' && !control.value.trim()
    ? { required: true }
    : null;
};

@Component({
  selector: 'app-manage-retailer-dialog',
  templateUrl: './manage-retailer-dialog.component.html',
  styleUrls: ['./manage-retailer-dialog.component.sass'],
})
export class ManageRetailerDialogComponent implements OnInit {
  form!: FormGroup;
  retailerList: any;
  programs: IProgram[] = [];
  editingProgramIndex: number | null = null;

  constructor(
    @Inject(MAT_DIALOG_DATA) public retailerParams: IRetailerDialog,
    private dialogRef: MatDialogRef<ManageRetailerDialogComponent>,
    private dialog: MatDialog,
    private fb: FormBuilder,
    private retailersService: RetailersService,
    private toastr: ToastrService
  ) {
    this.form = this.fb.group({
      fantasyName: ['', [Validators.required, requiredTrimmed]],
      retailerId: ['', [Validators.required, requiredTrimmed]],
      flowType: ['', [Validators.required]],
      comissao: ['', [
        Validators.min(0),
        Validators.max(100),
      ]],
      contaDebito: ['', [Validators.required, requiredTrimmed, Validators.maxLength(12)]],
      contaCredito: ['', [Validators.required, requiredTrimmed, Validators.maxLength(12)]],
      programas: this.fb.array([]),
    });

    if (this.retailerParams.action === 'edit') {
      const { fantasyName, retailerId, flowType, comissao, contaDebito, contaCredito, programas } = this.retailerParams;

      this.form.patchValue({
        fantasyName,
        retailerId,
        flowType,
        comissao,
        contaDebito,
        contaCredito
      });

      this.form.get('fantasyName')?.disable();
      this.form.get('retailerId')?.disable();
      this.form.get('flowType')?.disable();

      (programas ?? []).forEach((programa) => {
        this.programasArray.push(this.createProgramGroup(programa));
      });
    }
  }

  ngOnInit(): void {
    this.retailersService.getPrograms().subscribe({
      next: (programs) => {
        this.programs = programs ?? [];
      },
      error: (err: any) => {
        this.toastr.error(err.errors?.[0] ?? 'Não foi possível carregar os programas.');
      },
    });
  }

  get programasArray(): FormArray {
    return this.form.get('programas') as FormArray;
  }

  private createProgramGroup(program?: Partial<IRetailerProgram>): FormGroup {
    const group = this.fb.group({
      id: [program?.id ?? null],
      idProgramaSav: [program?.idProgramaSav ?? null, [Validators.required]],
      comissao: [program?.comissao ?? 0, [Validators.required, Validators.min(0), Validators.max(100)]],
      contaDebito: [program?.contaDebito ?? '', [Validators.maxLength(12)]],
      contaCredito: [program?.contaCredito ?? '', [Validators.maxLength(12)]],
    });

    group.get('comissao')?.valueChanges.subscribe(() => this.updateProgramRowValidators(group));
    this.updateProgramRowValidators(group);

    return group;
  }

  private updateProgramRowValidators(group: FormGroup) {
    const comissao = group.get('comissao');
    const contaDebito = group.get('contaDebito');
    const contaCredito = group.get('contaCredito');

    if (Number(comissao?.value) > 0) {
      contaDebito?.setValidators([Validators.required, requiredTrimmed, Validators.maxLength(12)]);
      contaCredito?.setValidators([Validators.required, requiredTrimmed, Validators.maxLength(12)]);
    } else {
      contaDebito?.setValidators([Validators.maxLength(12)]);
      contaCredito?.setValidators([Validators.maxLength(12)]);
    }

    contaDebito?.updateValueAndValidity({ emitEvent: false });
    contaCredito?.updateValueAndValidity({ emitEvent: false });
  }

  programOptionsFor(index: number): IProgram[] {
    const selectedIds = this.programasArray.controls
      .map((control, i) => (i !== index ? control.get('idProgramaSav')?.value : null))
      .filter((id) => id !== null && id !== undefined);

    return this.programs.filter((program) => !selectedIds.includes(program.id));
  }

  getProgramName(idProgramaSav: number): string {
    return this.programs.find((program) => program.id === idProgramaSav)?.name ?? '';
  }

  truncateTableValue(value: string | null | undefined): string {
    const maxLength = 12;

    return value && value.length > maxLength
      ? `${value.slice(0, maxLength)}...`
      : value ?? '-';
  }

  addProgramRow() {
    this.programasArray.push(this.createProgramGroup());
    this.editingProgramIndex = this.programasArray.length - 1;
  }

  editProgramRow(index: number) {
    this.editingProgramIndex = index;
  }

  confirmProgramRow(index: number) {
    const group = this.programasArray.at(index) as FormGroup;
    group.markAllAsTouched();

    if (group.valid) {
      this.editingProgramIndex = null;
    }
  }

  removeProgramRow(index: number) {
    this.programasArray.removeAt(index);

    if (this.editingProgramIndex === index) {
      this.editingProgramIndex = null;
    } else if (this.editingProgramIndex !== null && this.editingProgramIndex > index) {
      this.editingProgramIndex--;
    }
  }

  confirmRemoveProgramRow(index: number) {
    const confirmationDialog = this.dialog.open(ContentDialogGenericComponent, {
      data: {
        title: 'Remover comissão',
        message: 'Deseja remover esta configuração de comissão do programa?',
        buttons: {
          secondaryText: 'Cancelar',
          primaryText: 'Remover',
        },
      },
    });

    confirmationDialog.afterClosed().subscribe((confirmed) => {
      if (confirmed) {
        this.removeProgramRow(index);
      }
    });
  }

  save() {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    const { retailerId } = this.retailerParams;

    const { programas, ...formData } = this.form.getRawValue();

    const payload = {
      ...formData,
      programas: (programas as Array<IRetailerProgram & { id: string | null }>).map((programa) => ({
        ...(programa.id ? { id: programa.id } : {}),
        idProgramaSav: programa.idProgramaSav,
        descricao: this.getProgramName(programa.idProgramaSav),
        comissao: programa.comissao,
        contaCredito: programa.contaCredito,
        contaDebito: programa.contaDebito,
      })),
    };

    const action = this.retailerParams.action;
    const apiCall =
      action === 'edit'
        ? this.retailersService.updateRetailer(payload, retailerId)
        : this.retailersService.createRetailer(payload);

    apiCall.subscribe({
      next: (response: any) => {
        const actionDescription = action === 'edit' ? 'atualizado' : 'cadastrado';
        this.toastr.success(
          `Varejista ${formData.fantasyName} ${actionDescription} com sucesso.`
        );

        this.dialogRef.close({ up: true });
      },
      error: (err: any) => {
        this.toastr.error(err.errors[0]);
        this.dialogRef.close({ up: false });
      },
    });
  }
}
